<script>
	import * as Yup from "yup";
	import { useValidations, maskNumericValue } from "../ursCommon/Forms.svelte";
	import { baseUrl } from "../ursCommon/Common.svelte";
	import { Loader } from "../ursCommon/GlobalLoader.svelte";
	import {
		FormGroup,
		Input,
		Card,
		CardHeader,
		CardBody,
		CardText,
	} from "@sveltestrap/sveltestrap";
	import PhoneNumberInput, { PhoneTypes } from "./PhoneNumberInput.svelte";
	import * as Constants from "./Constants.js";

	let { disabled = true } = $props();

	let prompt;
	let faxNumberInput;
	let officeNumberInput;

	const { validateField, validateForm, getErrors, resetErrors, removeError } =
		useValidations();

	let formData = $state({
		npi: "",
		email: "",
		//webId: "",
		practitionerName: "",
		officeContactName: "",
		phoneNumber: "phone",
		faxNumber: "fax",
	});

	let errors = $derived(getErrors());
	let physicianHasErrors = $derived(
		Object.keys(errors).length ||
			faxNumberInput.hasErrors() ||
			officeNumberInput.hasErrors()
			? true
			: false,
	);
	const emptyToUndefined = (v) =>
		typeof v === "string" && v.trim() == "" ? undefined : v;
	let baseSchema = Yup.object({
		email: Yup.string()
			.trim()
			.transform(emptyToUndefined)
			.email("Please enter a valid email address")
			.required("Please enter an email address."),
			//.notRequired()
			//.test(
			//	'email-or-webId', 
			//	'Please provide either an Email address or Web ID.', 
			//	function() {
			//		const parent = this.parent || {};
			//		const { email, webId } = this.parent;
			//		return !!(email || webId);
			//}),
		//webId: Yup.string()
			//.trim()
			//.transform(emptyToUndefined)
			//.notRequired()
			//.test(
			//	'webId-or-email', 
			//	'Please provide either an Email address or Web ID.', 
			//	function() {
			//		const parent = this.parent || {};
			//		const { email, webId } = this.parent;
			//		return !!(email || webId);
			//}),
		npi: Yup.string()
			.required("NPI is required")
			.test("is-valid-npi", "Please enter a valid NPI", checkNpi),
		practitionerName: Yup.string()
			.matches(Constants.NAME_REGEX, Constants.NAME_MATCH_SUGGESTION)
			.required("Please enter the practitioners name."),
		officeContactName: Yup.string()
			.matches(Constants.NAME_REGEX, Constants.NAME_MATCH_SUGGESTION)
			.required("Please enter the name of the office contact."),
	});

	export function clearAllErrors() {
		resetErrors();
	}

	export function getValues() {
		var rtn = formData;
		rtn.phoneNumber = officeNumberInput.getValues().phoneNumber;
		rtn.faxNumber = faxNumberInput.getValues().phoneNumber;
		return rtn;
	}

	export function hasErrors() {
		return physicianHasErrors;
	}

	export function setValues(data) {
		formData.npi = data.npi;
		formData.email = data.email;
		formData.practitionerName = data.practitionerName;
		formData.officeContactName = data.officeContactName;
		//formData.webId = data.webId;
		resetErrors();
	}

	export async function validate() {
		await officeNumberInput.validate();
		await faxNumberInput.validate();
		await validateForm(baseSchema, formData);
	}

	function handleChange(fieldName, fieldValue) {
		const vars = new Map();
		vars.set(fieldName, fieldValue);
		const data = Object.fromEntries(vars);
		for (const key in data) {
			// console.log(`${key}: ${data[key]}`);
			validateField(baseSchema, key, formData);
		}
	}

	function checkNpi() {
		// Basic format checks
		if (typeof formData.npi !== "string" || !/^(\d{10})?$/.test(formData.npi))
			return false;
		const baseNine = "80840" + formData.npi.slice(0, 9);
		const checkDigit = Number(formData.npi[9]);
		// Luhn sum over the 9-digit sequence
		let sum = 0;
		// Process from right to left
		for (
			let i = baseNine.length - 1, posFromRight = 0;
			i >= 0;
			i--, posFromRight++
		) {
			let d = Number(baseNine[i]);
			// Double every second digit begining at the rightmost digit and proceding from right to left
			if (posFromRight % 2 === 0) {
				d *= 2;
				if (d > 9) d -= 9; // subtract 9 (equivalent to summing digits)
			}
			sum += d;
		}
		const computedCheck = (10 - (sum % 10)) % 10;
		return computedCheck === checkDigit;
	}

	function handleNpiInput(event) {
		const input = event.target;
		formData.npi = maskNumericValue(
			input,
			event.inputType,
			"9999999999",
			formData.npi,
		);
		validateField(baseSchema, "npi", formData);
	}
</script>

<main>
	<div class="row">
		<div class="col-xs-12 col-sm-12 col-md-4 col-lg-4">
			<FormGroup floating label="Practitioner Name">
				<Input
					type="text"
					bind:value={formData.practitionerName}
					onchange={(e) => handleChange("practitionerName", e.target.value)}
					invalid={Boolean(errors.practitionerName)}
					feedback={errors.practitionerName}
					{disabled}
				/>
			</FormGroup>
		</div>
		<div class="col-xs-12 col-sm-12 col-md-4 col-lg-4">
			<FormGroup floating label="Practitioner NPI">
				<Input
					type="text"
					bind:value={formData.npi}
					oninput={handleNpiInput}
					invalid={Boolean(errors.npi)}
					feedback={errors.npi}
					{disabled}
					inputmode="numeric"
					maxlength="10"
				/>
			</FormGroup>
		</div>
	</div>
	<div class="row">
		<div class="col-xs-12 col-sm-12 col-md-4 col-lg-4">
			<FormGroup floating label="Office Contact Person">
				<Input
					type="text"
					bind:value={formData.officeContactName}
					onchange={(e) => handleChange("officeContactName", e.target.value)}
					invalid={Boolean(errors.officeContactName)}
					feedback={errors.officeContactName}
					{disabled}
				/>
			</FormGroup>
		</div>
		<div class="col-xs-12 col-sm-12 col-md-4 col-lg-4">
			<PhoneNumberInput
				bind:this={officeNumberInput}
				prompt="Phone Number"
				phoneType={PhoneTypes.Office}
				{disabled}
				required="true"
				requiredErrorText="Please enter your office phone number"
			/>
		</div>
		<div class="col-xs-12 col-sm-12 col-md-4 col-lg-4">
			<PhoneNumberInput
				bind:this={faxNumberInput}
				prompt="Number"
				phoneType={PhoneTypes.Fax}
				{disabled}
				required="true"
				requiredErrorText="Please enter your fax number"
			/>
		</div>
	</div>
	<div class="row">
		<div class="col-sm-12 col-md-6">
			<FormGroup floating label="Office Email">
				<Input
					type="email"
					bind:value={formData.email}
					onchange={(e) => handleChange("email", e.target.value)}
					invalid={Boolean(errors.email)}
					feedback={errors.email}
					{disabled}
				/>
			</FormGroup>
		</div>
		<!-- <div class="col-sm-12 col-md-6">
			<FormGroup floating label="Web ID">
				<Input
					type="text"
					bind:value={formData.webId}
					onchange={(e) => handleChange("webId", e.target.value)}
					invalid={Boolean(errors.webId)}
					feedback={errors.webId}
					{disabled}
				/>
			</FormGroup>
		</div> -->
	</div>
	{#if formData.email}
	<div class="row">
		<div class="col-s-12 center">
			Responses regarding this pre-authorization request will be sent to {formData.email}			
		</div>
	</div>
	{/if}
</main>
